import { useState, useEffect } from 'react';
import type { DailyChallengeInfo, DailyLeaderboardEntry, DailyChallengeCompletePayload, PersonalRecordEntry } from '@flip7/shared';

interface DailyChallengeScreenProps {
  onStartDaily: (challengeId: string) => void;
  onBack: () => void;
  dailyResult: DailyChallengeCompletePayload | null;
}

const RECORD_LABELS: Record<string, string> = {
  highest_game_score: 'Highest Game Score',
  highest_round_score: 'Highest Round Score',
  most_flip7s: 'Most Flip 7s (single game)',
};

export function DailyChallengeScreen({ onStartDaily, onBack, dailyResult }: DailyChallengeScreenProps) {
  const [info, setInfo] = useState<DailyChallengeInfo | null>(null);
  const [leaderboard, setLeaderboard] = useState<DailyLeaderboardEntry[]>([]);
  const [records, setRecords] = useState<PersonalRecordEntry[]>([]);
  const [tab, setTab] = useState<'leaderboard' | 'records'>('leaderboard');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('flip7_access_token');
    if (!token) return;
    const apiBase = import.meta.env.VITE_API_URL || '';
    const headers = { 'Authorization': `Bearer ${token}` };
    setLoading(true);
    Promise.all([
      fetch(`${apiBase}/api/challenge/daily`, { headers }).then(r => r.json()),
      fetch(`${apiBase}/api/challenge/daily/leaderboard`, { headers }).then(r => r.json()),
      fetch(`${apiBase}/api/challenge/records`, { headers }).then(r => r.json()),
    ])
      .then(([dailyInfo, lb, recs]) => {
        setInfo(dailyInfo);
        if (Array.isArray(lb)) setLeaderboard(lb);
        if (Array.isArray(recs)) setRecords(recs);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dailyResult]);

  if (loading) {
    return (
      <div className="hero-panel">
        <div className="screen screen-center">
          <p className="text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-panel">
      <div className="screen screen--padded-top content-narrow" style={{ padding: 'var(--space-xl) var(--space-lg)' }}>
        <div className="page-header" style={{ marginBottom: 'var(--space-lg)' }}>
          <button onClick={onBack} className="back-btn">&larr;</button>
          <div style={{ flex: 1 }}>
            <h1 className="section-header" style={{ fontSize: '1.6rem' }}>Daily Challenge</h1>
            <p className="text-xs text-muted" style={{ margin: '2px 0 0' }}>
              {info?.date ?? 'Today'}
            </p>
          </div>
        </div>

        {/* Daily Result Overlay */}
        {dailyResult && (
          <div className="ornate-border badge-pop result-panel" style={{
            borderColor: dailyResult.won ? 'var(--gold)' : 'var(--color-bust)',
            background: dailyResult.won ? 'linear-gradient(180deg, #FFF8E1, var(--parchment))' : undefined,
          }}>
            <p className="result-panel__title" style={{
              color: dailyResult.won ? 'var(--gold)' : 'var(--color-bust)',
            }}>
              {dailyResult.won ? 'Victory!' : 'Defeated'}
            </p>
            <p className="text-body mt-sm" style={{ color: 'var(--text-primary)' }}>
              Score: {dailyResult.finalScore}
            </p>
            <p className="result-panel__detail">
              Rank #{dailyResult.rank} of {dailyResult.totalPlayers}
            </p>
            {dailyResult.coinsEarned > 0 && (
              <p className="text-body-sm text-gold font-bold mt-xs">
                +{dailyResult.coinsEarned} coins
              </p>
            )}
            {dailyResult.newRecords.length > 0 && (
              <div className="text-sm text-gold mt-sm">
                {dailyResult.newRecords.map(r => (
                  <p key={r.key}>New record: {RECORD_LABELS[r.key] ?? r.key} = {r.value}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Challenge Card */}
        {info && (
          <div className="ornate-border">
            <div className="flex-between">
              <span className="text-display">
                Today&apos;s Challenge
              </span>
              {info.variant !== 'standard' && (
                <span className={info.variant === 'vengeance-brutal' ? 'chip chip--brutal' : 'chip chip--vengeance'}>
                  {info.variant === 'vengeance-brutal' ? 'Brutal' : 'Vengeance'}
                </span>
              )}
            </div>
            <p className="text-sm text-muted mt-sm">
              {info.botCount} bot{info.botCount > 1 ? 's' : ''} ({info.botDifficulty}) | Target: {info.targetScore}
            </p>
            <button
              className="btn btn-primary w-full mt-md"
              disabled={info.alreadyPlayed}
              onClick={() => onStartDaily(info.id)}
            >
              {info.alreadyPlayed ? 'Already Played Today' : 'Play'}
            </button>
            {info.alreadyPlayed && info.playerResult && (
              <p className="text-sm text-muted text-center mt-sm">
                Your score: {info.playerResult.finalScore} {info.playerResult.won ? '(Won!)' : ''}
              </p>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="tab-bar">
          <button
            className={`btn btn-small ${tab === 'leaderboard' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab('leaderboard')}
          >
            Leaderboard
          </button>
          <button
            className={`btn btn-small ${tab === 'records' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab('records')}
          >
            Records
          </button>
        </div>

        {tab === 'leaderboard' && (
          <div className="flex-col" style={{ gap: '4px' }}>
            {leaderboard.length === 0 ? (
              <p className="text-body-sm text-muted text-center">
                No results yet. Be the first to play!
              </p>
            ) : (
              leaderboard.map((entry, i) => (
                <div key={entry.userId} className="ornate-border flex-between list-row" style={{
                  background: i === 0 ? 'linear-gradient(90deg, #FFF8E1, var(--parchment))' : undefined,
                }}>
                  <div className="flex-row gap-sm">
                    <span className="text-display-sm" style={{
                      color: i < 3 ? 'var(--gold)' : 'var(--text-muted)',
                      minWidth: '24px',
                    }}>
                      #{i + 1}
                    </span>
                    <span className="text-body">
                      {entry.userName}
                    </span>
                  </div>
                  <div className="flex-row gap-sm">
                    {entry.won && (
                      <span className="text-xs text-gold font-bold">Won</span>
                    )}
                    <span className="text-display-sm text-maroon">
                      {entry.finalScore}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'records' && (
          <div className="flex-col" style={{ gap: '4px' }}>
            {records.length === 0 ? (
              <p className="text-body-sm text-muted text-center">
                Play games to set personal records!
              </p>
            ) : (
              records.map(r => (
                <div key={r.key} className="ornate-border flex-between list-row">
                  <span className="text-body-sm">
                    {RECORD_LABELS[r.key] ?? r.key}
                  </span>
                  <span className="text-display-sm text-gold font-bold">
                    {r.value}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
